import { useState } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { Checkbox } from "@/components/ui/checkbox";
import { ArrowLeft, Check, Lock } from "lucide-react";
import { CryptoPaymentRequest, paymentAPI } from "@/services/payment";
import { PaypalCheckout } from "../components/PaypalCheckout";
import PageWithNavbar from "@/components/PageWithNavbar";

export interface PricingPlan {
  id: number;
  title: string;
  price: number;
  description: string;
  benefitList: string[];
}

export const CheckoutPage = () => {
  const location = useLocation();
  const navigate = useNavigate();
  const selectedPlan = location.state?.selectedPlan || null;
  const userInfo = location.state?.userInfo || null;

  // Detect result and anonymous info from query param
  const params = new URLSearchParams(location.search);
  const result = params.get("result");
  const isAnonymous = params.get("anonymous") === "true";
  const publicEmail = params.get("public");
  const anonymousPassword = params.get("private");
  
  // Remove unused card fields from formData
  const [formData, setFormData] = useState({
    email: userInfo?.email || "",
    fullName: userInfo ? `${userInfo.firstName} ${userInfo.lastName}` : "",
    paymentMethod: "card", // "card", "btc", "xmr"
  });

  const [isProcessing, setIsProcessing] = useState(false);
  const [showCryptoPayment, setShowCryptoPayment] = useState(false);
  const [eulaAccepted, setEulaAccepted] = useState(false);

  if (result === "success") {
    return (
      <div className="flex flex-col items-center justify-center min-h-screen bg-background">
        <Card className=" w-full text-center">
          <CardHeader>
            <CardTitle className="text-green-600 text-2xl">✅ Payment Successful!</CardTitle>
          </CardHeader>
          <CardContent>
            <p className="mb-6">Thank you for your purchase.<br />Your payment was processed successfully.</p>
            {isAnonymous && publicEmail && anonymousPassword && (
              <div className="mb-6 p-4 bg-blue-50 dark:bg-blue-900/20 border border-blue-200 dark:border-blue-800 rounded-lg">
                <h3 className="text-lg font-semibold text-blue-700 dark:text-blue-200 mb-2">Anonymous Account Created</h3>
                <p className="text-sm text-blue-700 dark:text-blue-300">Your anonymous account credentials:</p>
                <div className="mt-2 text-center">
                  <div><strong>Email:</strong> <span className="font-mono">{publicEmail}</span></div>
                  <div><strong>Password:</strong> <span className="font-mono">{anonymousPassword}</span></div>
                </div>
                <p className="text-xs text-muted-foreground mt-2">Please save these credentials securely. You will need them to access your account and downloads.</p>
              </div>
            )}
            <Button onClick={() => navigate("/profile")}>Download Doyen</Button>
            <Button onClick={() => navigate("/")}>Return to Home</Button>
          </CardContent>
        </Card>
      </div>
    );
  }
  if (result === "cancel") {
    return (
      <div className="flex flex-col items-center justify-center min-h-screen bg-background">
        <Card className="max-w-md w-full text-center">
          <CardHeader>
            <CardTitle className="text-red-600 text-2xl">❌ Payment Cancelled</CardTitle>
          </CardHeader>
          <CardContent>
            <p className="mb-6">Your payment was not completed.<br />You can try again or contact support if you need help.</p>
            <Button onClick={() => navigate("/")}>Return to Home</Button>
          </CardContent>
        </Card>
      </div>
    );
  }

  const handleCryptoCheckout = async () => {
    setIsProcessing(true);
    try {
      console.log("Processing Crypto payment for plan:", selectedPlan);
      const paymentRequest: CryptoPaymentRequest = { plan: selectedPlan.id, email: formData.email, cryptoType: 'bitcoin' };
      const data = await paymentAPI.processCryptoPayment(paymentRequest);
      console.log("Crypto payment response:", data);
      if (data.sessionId) {
        console.log("Crypto payment session id:", data.sessionId);
      }
      if (data.url) {
        // Redirect to invoice URL
        window.location.href = data.url;
      } else {
        alert("No invoice URL returned. Please try again or contact support."); 
      }
    } catch (err) {
      console.error("Crypto checkout error:", err);
      alert("Crypto checkout error. Please try again.");
    } finally {
      setIsProcessing(false);
    }
  };
  
  if (!selectedPlan && !result) {
    return (
      <div className="container mx-auto px-4 py-24 text-center">
        <h1 className="text-2xl font-bold mb-4">No plan selected</h1>
        <Button onClick={() => navigate("/")} className="mb-4">
          <ArrowLeft className="mr-2 h-4 w-4" />
          Back to Home
        </Button>
      </div>
    );
  }

  const subtotal = selectedPlan.price;
  //const tax = subtotal * 0.08; // 8% tax
  //const total = subtotal + tax;

  return (
  <PageWithNavbar>
    <div className="min-h-screen bg-background">
      {}
      <div className="container mx-auto px-4 py-8">
        <div className="grid lg:grid-cols-2 gap-8">
          {/* Order Summary */}
          <div className="order-2 lg:order-1">
            <Card className="bg-slate-800 border-slate-700 text-white">
              <CardHeader>
                <CardTitle className="flex items-center gap-2 text-white">
                  <Lock className="h-5 w-5" />
                  Payment Information
                </CardTitle>
                <CardDescription className="text-slate-300">
                  Your payment information is secure and encrypted
                </CardDescription>
              </CardHeader>              
              <CardContent>
                {/* User Info Confirmation */}
                {userInfo && (
                  <div className="mb-6 p-4 bg-green-50 dark:bg-green-900/20 border border-green-200 dark:border-green-800 rounded-lg">
                    <div className="flex items-center gap-2 mb-2">
                      <Check className="h-4 w-4 text-green-600" />
                      <span className="font-medium text-sm text-green-800 dark:text-green-200">
                        Account Information Confirmed
                      </span>
                    </div>
                    <p className="text-sm text-green-700 dark:text-green-300">
                      Your account details have been pre-filled from registration.
                    </p>
                  </div>
                )}
                
                <form className="space-y-4">
                  {/* Payment Method Selection */}
                  <div className="space-y-2">
                    <label className="text-sm font-medium text-slate-100">Payment Method</label>
                    <div className="grid grid-cols-3 gap-2">
                      <Button
                        type="button"
                        onClick={() => {
                          setFormData(prev => ({ ...prev, paymentMethod: "card" }));
                          setShowCryptoPayment(false);
                        }}
                        className="h-12"
                      >
                        Card
                      </Button>
                      <Button
                        type="button"
                        onClick={() => {
                          setFormData(prev => ({ ...prev, paymentMethod: "btc" }));
                          setShowCryptoPayment(true);
                        }}
                        className="h-12"
                      >
                        Crypto
                      </Button>
                    </div>
                  </div>
                  {/* Payment Metadata */}
                  <div className="space-y-2">
                    <label htmlFor="email" className="text-sm font-medium text-slate-100">
                      Email Address {userInfo && <span className="text-green-600">✓</span>}
                    </label>
                    <Input
                      id="email"
                      name="email"
                      type="email"
                      placeholder="your@email.com"
                      value={formData.email}
                      onChange={e => setFormData(prev => ({ ...prev, email: e.target.value }))}
                      disabled={!!userInfo}
                      className={userInfo ? "bg-slate-700 text-slate-200 border-slate-600" : "bg-slate-700 text-slate-100 border-slate-600 placeholder:text-slate-400"}
                      required
                    />
                    {userInfo && (
                      <p className="text-xs text-slate-400">
                        Pre-filled from registration
                      </p>
                    )}
                  </div>
                  
                  {/* EULA Acceptance */}
                  <div className="space-y-2">
                    <div className="flex items-start space-x-2">
                      <Checkbox
                        checked={eulaAccepted}
                        onCheckedChange={setEulaAccepted}
                        id="eula-checkbox"
                      />
                      <label
                        htmlFor="eula-checkbox"
                        className="text-sm leading-none text-slate-100 peer-disabled:cursor-not-allowed peer-disabled:opacity-70"
                      >
                        I have read and agree to the{" "}
                        <a
                          href="/eula.txt"
                          target="_blank"
                          rel="noopener noreferrer"
                          className="text-blue-300 hover:underline"
                        >
                          End User License Agreement (EULA)
                        </a>
                      </label>
                    </div>
                  </div>
                  
                  {!showCryptoPayment ? (
                    <>
                      <div className="mb-4 p-4 bg-white border border-slate-600 rounded-lg">
                      <PaypalCheckout 
                        plan={selectedPlan.id} 
                        email={formData.email}
                        disabled={!eulaAccepted}
                      />
                      </div>
                    </>
                  ) : (
                    <>
                      <Button
                        type="button"
                        className="w-full"
                        disabled={isProcessing || !formData.email || !eulaAccepted}
                        onClick={handleCryptoCheckout}
                      >
                        {isProcessing ? (
                          "Redirecting to BTCPayServer..."
                        ) : (
                          <>
                            <Lock className="mr-2 h-4 w-4" />
                            Checkout with Crypto (BTCPayServer)
                          </>
                        )}
                      </Button>
                    </>
                  )}
                </form>
              </CardContent>
            </Card>
          </div>

          {/* Plan Summary */}
          <div className="order-1 lg:order-2">
            <Card className="bg-slate-800 border-slate-700 text-white">
              <CardHeader>
                <CardTitle className="text-white">Order Summary</CardTitle>
              </CardHeader>
              <CardContent className="space-y-4">
                <div className="flex items-start justify-between">
                  <div>
                    <h3 className="font-semibold text-lg">{selectedPlan.title} Plan</h3>
                    <p className="text-sm text-slate-300">{selectedPlan.description}</p>
                  </div>
                </div>
                
                <hr className="border-slate-600" />

                <div className="space-y-2">
                  <div className="flex justify-between">
                    <span className="text-slate-200">Subtotal</span>
                    <span className="text-slate-200">${subtotal.toFixed(2)}</span>
                  </div>
                  <hr className="border-slate-600" />                  
                  <div className="flex justify-between font-semibold text-lg">
                    <span>Total</span>
                    <span>${subtotal.toFixed(2)} (1 year)</span>
                  </div>
                </div> 

                <p className="mt-4 text-sm"><strong>License Terms</strong><br/>Your purchase includes <strong>one year of updates and support</strong>. After this period, you may continue using the last version within your license period indefinitely but will not receive further updates, priority support or have access to phone support. To continue receiving updates and support, you will have to renew your license.</p>
              </CardContent>
            </Card>
          </div>
        </div>
      </div>
    </div>
    </PageWithNavbar>
  );
};

export default CheckoutPage;
